import React, { useCallback, useEffect, useState } from 'react';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
} from '@backstage/core-components';
import {
  Button,
  FormControl,
  InputLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { useArtaEdition } from '../../artaEdition';
import StarsIcon from '@material-ui/icons/Stars';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

interface TemplatePack {
  name: string;
  templatePath: string;
}

interface TemplateRegistry {
  repository: string;
  repositoryUrl: string;
  catalogIndexPath: string;
  packs: TemplatePack[];
}

interface TemplateDraft {
  packName: string;
  title: string;
  summary: string;
  provider: string;
  estimatedMonthlyCost: number;
  files: Array<{ path: string; content: string }>;
  warnings: string[];
  review: {
    status: 'passed' | 'failed';
    draftDigest: string;
    findings: Array<{
      id: string;
      severity: 'info' | 'warning' | 'error';
      category: 'security' | 'cost' | 'validation';
      message: string;
      file?: string;
    }>;
  };
}

export const TemplateManagementPage = () => {
  const { edition, features } = useArtaEdition();
  const [selectedCloud, setSelectedCloud] = useState<'azure' | 'aws'>('azure');
  const cloud = edition === 'multicloud' ? selectedCloud : edition;
  const selector =
    edition === 'multicloud' ? (
      <FormControl variant="outlined" margin="normal">
        <InputLabel>Cloud</InputLabel>
        <Select
          label="Cloud"
          value={cloud}
          onChange={event =>
            setSelectedCloud(event.target.value as 'azure' | 'aws')
          }
        >
          <MenuItem value="azure">Azure / Azure DevOps</MenuItem>
          <MenuItem value="aws">AWS / GitLab</MenuItem>
        </Select>
      </FormControl>
    ) : null;
  return (
    <TemplateManagementContent
      key={cloud}
      cloud={cloud}
      selector={selector}
      assistantEnabled={features?.templateAssistant ?? true}
    />
  );
};

const TemplateManagementContent = ({
  cloud,
  selector,
  assistantEnabled,
}: {
  cloud: 'azure' | 'aws';
  selector: React.ReactNode;
  assistantEnabled: boolean;
}) => {
  const aws = cloud === 'aws';
  const reviewName = aws ? 'Merge Request' : 'Pull Request';
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const [registry, setRegistry] = useState<TemplateRegistry>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [draft, setDraft] = useState<TemplateDraft>();
  const [selectedFile, setSelectedFile] = useState('');
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [removing, setRemoving] = useState('');
  const [pullRequestUrl, setPullRequestUrl] = useState('');

  const callBackend = useCallback(
    async (path: string, init?: RequestInit) => {
      const baseUrl = await discoveryApi.getBaseUrl(
        aws ? 'aws-apps-backend' : 'azure-apps',
      );
      const credentials = await identityApi.getCredentials();
      return fetch(`${baseUrl}${path}`, {
        ...init,
        headers: {
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(credentials.token
            ? { Authorization: `Bearer ${credentials.token}` }
            : {}),
          ...init?.headers,
        },
      });
    },
    [aws, discoveryApi, identityApi],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await callBackend('/template-packs');
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          body.error ?? `Request failed with HTTP ${response.status}`,
        );
      }
      setRegistry(await response.json());
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setLoading(false);
    }
  }, [callBackend]);

  const generate = async () => {
    setGenerating(true);
    setError(undefined);
    setDraft(undefined);
    setPullRequestUrl('');
    try {
      const response = await callBackend('/template-assistant/generate', {
        method: 'POST',
        body: JSON.stringify({ prompt }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(body.error ?? 'Template generation failed');
      setDraft(body);
      setSelectedFile(body.files?.[0]?.path ?? '');
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setGenerating(false);
    }
  };

  const publish = async () => {
    if (
      !draft ||
      !window.confirm(
        `Create a draft ${reviewName.toLowerCase()} for '${
          draft.packName
        }' in ${registry?.repository}?`,
      )
    )
      return;
    setPublishing(true);
    setError(undefined);
    try {
      const response = await callBackend('/template-assistant/publish', {
        method: 'POST',
        body: JSON.stringify({ draft }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(body.error ?? `${reviewName} creation failed`);
      setPullRequestUrl(body.pullRequestUrl);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setPublishing(false);
    }
  };

  const removePack = async (pack: TemplatePack) => {
    if (
      !window.confirm(
        `Create a draft ${reviewName.toLowerCase()} to remove '${
          pack.name
        }'? Existing entities and ${
          aws ? 'AWS' : 'Azure'
        } resources will not be deleted.`,
      )
    )
      return;
    setRemoving(pack.name);
    setError(undefined);
    setPullRequestUrl('');
    try {
      const response = await callBackend(
        `/template-packs/${encodeURIComponent(pack.name)}`,
        { method: 'DELETE' },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(body.error ?? 'Template removal failed');
      setPullRequestUrl(body.pullRequestUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setRemoving('');
    }
  };

  const activeFile = draft?.files.find(file => file.path === selectedFile);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Page themeId="tool">
      <Header
        title="Template Management"
        subtitle={`Manage the customer-owned ${
          aws ? 'AWS GitLab' : 'Azure DevOps'
        } template registry`}
      />
      <Content>
        {selector}
        {loading && <Progress />}
        {error && <ResponseErrorPanel error={error} />}
        {registry && (
          <Grid container spacing={3}>
            {assistantEnabled && (
              <Grid item xs={12}>
                <InfoCard title="AI Template Assistant">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 16,
                    }}
                  >
                    <StarsIcon
                      color="primary"
                      style={{ fontSize: 32, marginRight: 10 }}
                    />
                  </div>
                  <Typography paragraph>
                    Describe the {aws ? 'AWS' : 'Azure'} architecture you want.
                    The assistant creates a draft checked for structure and
                    supported actions. Review the generated infrastructure
                    before merging its {reviewName.toLowerCase()}.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    variant="outlined"
                    label="Architecture description"
                    placeholder={
                      aws
                        ? 'Create an AWS ECS application template using GitLab CI, an existing AWS environment provider, and least-privilege IAM roles...'
                        : 'Create an Azure Synapse environment provider with a managed virtual network and Terraform deployment pipeline...'
                    }
                    value={prompt}
                    onChange={event => setPrompt(event.target.value)}
                  />
                  <div style={{ marginTop: 16 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={generating || prompt.trim().length < 20}
                      onClick={generate}
                    >
                      {generating ? 'Generating...' : 'Generate Draft with AI'}
                    </Button>
                  </div>
                  {generating && <Progress />}
                  {draft && (
                    <div style={{ marginTop: 24 }}>
                      <Typography variant="h6">{draft.title}</Typography>
                      <Typography paragraph>{draft.summary}</Typography>
                      <Typography paragraph>
                        Provider: <strong>{draft.provider}</strong> · Pack:{' '}
                        <code>{draft.packName}</code>
                      </Typography>
                      <Typography paragraph>
                        Reviewed monthly estimate:{' '}
                        <strong>{draft.estimatedMonthlyCost}</strong> · Review:{' '}
                        <strong>{draft.review.status.toUpperCase()}</strong> ·
                        Evidence:{' '}
                        <code>{draft.review.draftDigest.slice(0, 12)}</code>
                      </Typography>
                      {draft.review.findings.map(finding => (
                        <Typography
                          key={`${finding.id}-${finding.file ?? ''}`}
                          color={
                            finding.severity === 'error'
                              ? 'error'
                              : 'textPrimary'
                          }
                          paragraph
                        >
                          [{finding.category}] {finding.message}
                          {finding.file ? ` (${finding.file})` : ''}
                        </Typography>
                      ))}
                      {draft.warnings.map(warning => (
                        <Typography key={warning} color="error" paragraph>
                          {warning}
                        </Typography>
                      ))}
                      <FormControl fullWidth variant="outlined" margin="normal">
                        <InputLabel>Generated File</InputLabel>
                        <Select
                          label="Generated File"
                          value={selectedFile}
                          onChange={event =>
                            setSelectedFile(String(event.target.value))
                          }
                        >
                          {draft.files.map(file => (
                            <MenuItem key={file.path} value={file.path}>
                              {file.path}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {activeFile && (
                        <pre
                          style={{
                            maxHeight: 480,
                            overflow: 'auto',
                            padding: 16,
                            borderRadius: 4,
                            background: '#17202a',
                            color: '#f4f6f7',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {activeFile.content}
                        </pre>
                      )}
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={
                          publishing || draft.review.status !== 'passed'
                        }
                        onClick={publish}
                      >
                        {publishing
                          ? `Creating ${reviewName}...`
                          : `Create Review ${reviewName}`}
                      </Button>
                      {pullRequestUrl && (
                        <Button
                          style={{ marginLeft: 12 }}
                          href={pullRequestUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open {reviewName}
                        </Button>
                      )}
                    </div>
                  )}
                </InfoCard>
              </Grid>
            )}
            <Grid item xs={12} md={8}>
              <Table
                title={`Installed Template Packs (${registry.packs.length})`}
                options={{ paging: false, search: true }}
                columns={[
                  { title: 'Pack', field: 'name' },
                  { title: 'Template', field: 'templatePath' },
                ]}
                data={registry.packs}
                actions={[
                  {
                    icon: () => <DeleteOutlineIcon />,
                    tooltip: `Create removal ${reviewName.toLowerCase()}`,
                    onClick: (_event, rowData) => {
                      if (!removing && !Array.isArray(rowData)) {
                        removePack(rowData as TemplatePack);
                      }
                    },
                  },
                  {
                    icon: () => <RefreshIcon />,
                    tooltip: 'Refresh template registry',
                    isFreeAction: true,
                    onClick: load,
                  },
                ]}
              />
              {pullRequestUrl && (
                <Button
                  style={{ marginTop: 12 }}
                  href={pullRequestUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Pending Template {reviewName}
                </Button>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard title="Add or Remove Packs">
                <Typography paragraph>
                  Add a pack under <code>templates/&lt;pack-name&gt;/</code> or
                  remove its directory on a branch. Run the repository index and
                  validation scripts, then review and merge the{' '}
                  {reviewName.toLowerCase()}.
                </Typography>
                <Typography paragraph>
                  The catalog index is maintained at{' '}
                  <code>{registry.catalogIndexPath}</code>.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  href={registry.repositoryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open {registry.repository}
                </Button>
              </InfoCard>
            </Grid>
          </Grid>
        )}
      </Content>
    </Page>
  );
};
